import { lazy, Suspense, useEffect, useRef, useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Hero from "./components/Hero";
import Stats from "./components/Stats";
import Manifesto from "./components/Manifesto";

const ChooseYourPath = lazy(() => import("./components/ChooseYourPath"));
const WhatIDo = lazy(() => import("./components/WhatIDo"));
const Process = lazy(() => import("./components/Process"));
const Team = lazy(() => import("./components/Team"));
const Projects = lazy(() => import("./components/Projects"));
const YouTubeSection = lazy(() => import("./components/YouTubeSection"));
const NewsletterSection = lazy(() => import("./components/NewsletterSection"));
const FinalCTA = lazy(() => import("./components/FinalCTA"));

function SectionFallback({ minHeight = 480 }: { minHeight?: number }) {
  return <div style={{ minHeight }} aria-hidden />;
}

function DeferredSection({
  children,
  minHeight,
  anchorId,
}: {
  children: React.ReactNode;
  minHeight: number;
  // Stable scroll anchor that lives on the always-present wrapper, so in-page
  // navigation resolves even before the lazy section has mounted.
  anchorId?: string;
}) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    const node = anchorRef.current;
    if (!node || shouldRender) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setShouldRender(true);
        observer.disconnect();
      },
      { rootMargin: "600px 0px" }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [shouldRender]);

  return (
    <div ref={anchorRef} id={anchorId} className="scroll-mt-20">
      {shouldRender ? children : <SectionFallback minHeight={minHeight} />}
    </div>
  );
}

export default function Home() {
  return (
    <div className="bg-ink min-h-screen">
      <Navbar />
      <main>
        <section>
          <Hero />
        </section>
        <section>
          <Stats />
        </section>

        {/* Everything below the metrics sits on a single ambient texture field
            (warm topo on light, green grain-glow on dark). The fixed layer is
            clipped to this container so it starts right under Stats and runs to
            the footer; sections inside use semi-transparent fills so it shows. */}
        <div className="relative isolate">
          <div className="ambient-texture absolute inset-0 -z-10 pointer-events-none" aria-hidden />

          <section>
            <Manifesto />
          </section>
          <DeferredSection minHeight={320} anchorId="choose-your-path">
          <Suspense fallback={<SectionFallback minHeight={320} />}>
            <ChooseYourPath />
          </Suspense>
        </DeferredSection>
        <DeferredSection minHeight={760} anchorId="what-i-do">
          <Suspense fallback={<SectionFallback minHeight={760} />}>
            <WhatIDo />
          </Suspense>
        </DeferredSection>
        <DeferredSection minHeight={820} anchorId="process">
          <Suspense fallback={<SectionFallback minHeight={820} />}>
            <Process />
          </Suspense>
        </DeferredSection>
        <DeferredSection minHeight={900} anchorId="team">
          <Suspense fallback={<SectionFallback minHeight={900} />}>
            <Team />
          </Suspense>
        </DeferredSection>
        <DeferredSection minHeight={680} anchorId="projects">
          <Suspense fallback={<SectionFallback minHeight={680} />}>
            <Projects />
          </Suspense>
        </DeferredSection>
        <DeferredSection minHeight={720} anchorId="youtube">
          <Suspense fallback={<SectionFallback minHeight={720} />}>
            <YouTubeSection />
          </Suspense>
        </DeferredSection>
        <DeferredSection minHeight={420} anchorId="newsletter">
          <Suspense fallback={<SectionFallback minHeight={420} />}>
            <NewsletterSection />
          </Suspense>
        </DeferredSection>
          <DeferredSection minHeight={760} anchorId="final-cta">
            <Suspense fallback={<SectionFallback minHeight={760} />}>
              <FinalCTA />
            </Suspense>
          </DeferredSection>
        </div>
      </main>
    </div>
  );
}
